import { useState, useEffect, useCallback, useRef } from 'react';
import PixelDaniel, { AnimationState, Direction } from './PixelDaniel';
import SpeechBubble, { getRandomMessage, getClickMessage, getSectionMessage, RANDOM_MESSAGES } from './SpeechBubble';
import PixelProp, { PropType } from './PixelProps';
import PixelScene, { SceneType } from './PixelScenes';
import './DanielController.css';

type ActionType =
  | 'idle'
  | 'walk'
  | 'speak'
  | 'wave'
  | 'point'
  | 'sleep'
  | 'coffee'
  | 'sign'
  | 'zoom-across'
  | 'scene'
  | 'climb'
  | 'walk-to-grab'
  | 'grab-element';

type VehicleType = 'donkey' | 'none';

interface Position {
  x: number;
  y: number;
}

interface WalkTarget {
  x: number;
  y: number;
}

interface GrabbedElement {
  html: string;
  width: number;
  height: number;
  originalElement: HTMLElement;
}

// Selectors for elements that can be grabbed (project cards only)
const GRABBABLE_SELECTORS = [
  '.glass.p-4.md\\:p-6.rounded-xl', // Project cards
];

const DanielController = () => {
  const [position, setPosition] = useState<Position>({ x: 50, y: window.innerHeight - 120 });
  const [animation, setAnimation] = useState<AnimationState>('idle');
  const [direction, setDirection] = useState<Direction>('right');
  const [currentAction, setCurrentAction] = useState<ActionType>('idle');
  const [speechMessage, setSpeechMessage] = useState('');
  const [showSpeech, setShowSpeech] = useState(false);
  const [activeProp, setActiveProp] = useState<PropType | null>(null);
  const [activeScene, setActiveScene] = useState<SceneType | null>(null);
  const [currentSection, setCurrentSection] = useState('hero');
  const [isInteracting, setIsInteracting] = useState(false);
  const [isHoldingSign, setIsHoldingSign] = useState(false);
  const [vehicle, setVehicle] = useState<VehicleType>('none');
  const [showDustCloud, setShowDustCloud] = useState(false);
  const [isOffscreen, setIsOffscreen] = useState(false);
  const [grabbedElement, setGrabbedElement] = useState<GrabbedElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragRotation, setDragRotation] = useState(0);
  const [velocity, setVelocity] = useState<Position>({ x: 0, y: 0 });
  const [isWearingHardhat, setIsWearingHardhat] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const walkTargetRef = useRef<WalkTarget | null>(null);
  const actionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedToElementRef = useRef<string | null>(null);
  const dragStartRef = useRef<Position | null>(null);
  const lastPosRef = useRef<Position>({ x: 0, y: 0 });
  const dragMessagesShown = useRef(0);
  const pendingGrabRef = useRef<HTMLElement | null>(null);
  const grabbedElementRef = useRef<HTMLElement | null>(null);

  // Get a random Y position within viewport
  const getRandomY = useCallback(() => {
    const minY = 100;
    const maxY = window.innerHeight - 120;
    return minY + Math.random() * (maxY - minY);
  }, []);

  // Lock position to an element's height
  const lockToElement = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const targetY = Math.max(100, Math.min(window.innerHeight - 120, rect.top + rect.height / 2));
      lockedToElementRef.current = elementId;
      return targetY;
    }
    return null;
  }, []);

  // Detect current section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'writing', 'projects', 'experience', 'recommendations', 'support', 'contact'];
      const windowHeight = window.innerHeight;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            setCurrentSection(sectionId);
            break;
          }
        }
      }

      // Update position if locked to element
      if (lockedToElementRef.current) {
        const element = document.getElementById(lockedToElementRef.current);
        if (element) {
          const rect = element.getBoundingClientRect();
          const targetY = Math.max(100, Math.min(window.innerHeight - 120, rect.top + rect.height / 2));
          setPosition(prev => ({ ...prev, y: targetY }));
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Clear action and reset to idle
  const resetToIdle = useCallback(() => {
    setAnimation('idle');
    setCurrentAction('idle');
    setShowSpeech(false);
    setActiveProp(null);
    setActiveScene(null);
    setIsInteracting(false);
    setIsHoldingSign(false);
    setVehicle('none');
    setShowDustCloud(false);
    setIsWearingHardhat(false);
    lockedToElementRef.current = null;
    pendingGrabRef.current = null;

    // Restore grabbed element visibility using ref (avoids closure issues)
    if (grabbedElementRef.current) {
      const el = grabbedElementRef.current;
      // Clear all inline styles we set
      el.style.removeProperty('visibility');
      el.style.removeProperty('opacity');
      el.style.removeProperty('transition');
      grabbedElementRef.current = null;
    }
    setGrabbedElement(null);
  }, []);

  // Find the closest grabbable element to Daniel's current position
  const findClosestGrabbableElement = useCallback(() => {
    // Find all grabbable elements currently in viewport
    const allGrabbable: { element: HTMLElement; rect: DOMRect; distance: number }[] = [];

    GRABBABLE_SELECTORS.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          const rect = el.getBoundingClientRect();
          // Only grab elements that are in viewport
          if (rect.top > 0 && rect.bottom < window.innerHeight && rect.left > 0 && rect.right < window.innerWidth) {
            // Calculate distance from Daniel to element center
            const elemCenterX = rect.left + rect.width / 2;
            const elemCenterY = rect.top + rect.height / 2;
            const dx = elemCenterX - position.x;
            const dy = elemCenterY - position.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            allGrabbable.push({ element: el as HTMLElement, rect, distance });
          }
        });
      } catch {
        // Invalid selector, skip
      }
    });

    if (allGrabbable.length === 0) return null;

    // Find the closest element
    allGrabbable.sort((a, b) => a.distance - b.distance);
    const closest = allGrabbable[0];

    return {
      element: closest.element,
      targetX: Math.max(20, Math.min(window.innerWidth - 100, closest.rect.left - 60)),
      targetY: Math.max(100, Math.min(window.innerHeight - 120, closest.rect.top + closest.rect.height / 2)),
    };
  }, [position.x, position.y]);

  // Show a speech bubble
  const showMessage = useCallback((message: string) => {
    setSpeechMessage(message);
    setShowSpeech(true);
  }, []);

  // Actually grab the element (called after walking to it)
  const grabElement = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();

    // Clone the element's appearance
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.cssText = window.getComputedStyle(element).cssText;

    // Store ref for restoration (avoids closure issues with setTimeout)
    grabbedElementRef.current = element;

    // Hide the original with transition
    element.style.transition = 'opacity 0.2s ease';
    element.style.opacity = '0';

    // After fade, hide visibility
    setTimeout(() => {
      element.style.visibility = 'hidden';
    }, 200);

    setGrabbedElement({
      html: clone.outerHTML,
      width: Math.min(rect.width, 200),
      height: Math.min(rect.height, 150),
      originalElement: element,
    });

    setAnimation('pushing');
    showMessage("I'm helping!");

    // Put it back after 4 seconds
    setTimeout(resetToIdle, 4000);
  }, [showMessage, resetToIdle]);

  // Walk to a position (X and Y)
  const walkTo = useCallback((target: WalkTarget) => {
    walkTargetRef.current = target;
    setCurrentAction('walk');
    setAnimation('walking');
    setDirection(target.x > position.x ? 'right' : 'left');
  }, [position.x]);

  // Ragdoll drag messages
  const DRAG_MESSAGES = [
    "Ow ow ow!",
    "Hey! Stop!",
    "Put me down!",
    "This is assault!",
    "AAHHH!",
    "Not cool!",
    "Mercy!",
    "I bruise easily!",
    "Help!",
    "Whyyyyy!",
  ];

  // Handle mouse/touch down - start drag
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isOffscreen) return;

    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setIsInteracting(true);
    setAnimation('surprised');
    setCurrentAction('idle');
    walkTargetRef.current = null;

    dragStartRef.current = { x: clientX - position.x, y: clientY - position.y };
    lastPosRef.current = { x: clientX, y: clientY };
    dragMessagesShown.current = 0;

    showMessage(getClickMessage());
  }, [isOffscreen, position, showMessage]);

  // Handle mouse/touch move - drag
  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging || !dragStartRef.current) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Calculate velocity for ragdoll feel
    const vx = clientX - lastPosRef.current.x;
    const vy = clientY - lastPosRef.current.y;
    setVelocity({ x: vx, y: vy });

    // Random rotation based on velocity for ragdoll effect
    setDragRotation(vx * 2 + (Math.random() - 0.5) * 10);

    // Update position
    const newX = clientX - dragStartRef.current.x;
    const newY = clientY - dragStartRef.current.y;
    setPosition({ x: newX, y: newY });

    lastPosRef.current = { x: clientX, y: clientY };

    // Show pain messages occasionally while dragging
    if (Math.random() < 0.03 && dragMessagesShown.current < 5) {
      dragMessagesShown.current++;
      showMessage(DRAG_MESSAGES[Math.floor(Math.random() * DRAG_MESSAGES.length)]);
    }
  }, [isDragging, showMessage]);

  // Handle mouse/touch up - release with physics
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    dragStartRef.current = null;

    // Apply momentum - character flies based on velocity
    const throwVelocity = { x: velocity.x * 3, y: velocity.y * 3 };
    let currentVel = { ...throwVelocity };
    let currentPos = { ...position };

    const applyPhysics = () => {
      // Apply friction
      currentVel.x *= 0.92;
      currentVel.y *= 0.92;

      // Apply gravity
      currentVel.y += 0.8;

      // Update position
      currentPos.x += currentVel.x;
      currentPos.y += currentVel.y;

      // Bounce off edges
      if (currentPos.x < 0) {
        currentPos.x = 0;
        currentVel.x *= -0.5;
      }
      if (currentPos.x > window.innerWidth - 60) {
        currentPos.x = window.innerWidth - 60;
        currentVel.x *= -0.5;
      }
      if (currentPos.y < 50) {
        currentPos.y = 50;
        currentVel.y *= -0.5;
      }
      if (currentPos.y > window.innerHeight - 100) {
        currentPos.y = window.innerHeight - 100;
        currentVel.y *= -0.5;
      }

      // Decay rotation
      setDragRotation(prev => prev * 0.9);
      setPosition({ ...currentPos });

      // Continue physics until settled
      if (Math.abs(currentVel.x) > 0.5 || Math.abs(currentVel.y) > 0.5) {
        requestAnimationFrame(applyPhysics);
      } else {
        // Settled - recover
        setDragRotation(0);
        setTimeout(() => {
          setAnimation('idle');
          setIsInteracting(false);
          showMessage("...ow");
        }, 500);
      }
    };

    requestAnimationFrame(applyPhysics);
  }, [isDragging, velocity, position, showMessage]);

  // Global mouse/touch listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);

      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Zoom across screen on vehicle
  const zoomAcross = useCallback((vehicleType: VehicleType) => {
    setIsOffscreen(true);
    setVehicle(vehicleType);
    setAnimation('riding');
    setShowDustCloud(true);

    // Random Y position for the zoom
    const zoomY = getRandomY();

    // Start from left or right
    const fromLeft = Math.random() > 0.5;
    const startX = fromLeft ? -120 : window.innerWidth + 120;
    const endX = fromLeft ? window.innerWidth + 120 : -120;

    setDirection(fromLeft ? 'right' : 'left');
    setPosition({ x: startX, y: zoomY });

    const messages = [
      "Yeehaw!",
      "Coming through!",
      "Make way!",
      "Zoom zoom!",
      "Later, nerds!",
    ];
    showMessage(messages[Math.floor(Math.random() * messages.length)]);

    const duration = 6000; // Slower so users can see it
    const startTime = Date.now();

    const animateZoom = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-in-out for natural feel
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const currentX = startX + (endX - startX) * eased;

      setPosition(prev => ({ ...prev, x: currentX }));

      if (progress < 1) {
        requestAnimationFrame(animateZoom);
      } else {
        // Come back after a bit
        setTimeout(() => {
          setIsOffscreen(false);
          setPosition({ x: 50, y: window.innerHeight - 120 });
          resetToIdle();
        }, 500);
      }
    };

    requestAnimationFrame(animateZoom);
  }, [getRandomY, showMessage, resetToIdle]);

  // Random action picker
  const pickRandomAction = useCallback(() => {
    if (isInteracting || isOffscreen) return;

    const actions: ActionType[] = [
      'walk', 'walk', 'walk', // Walking
      'climb', 'climb', // Move up/down
      'speak',
      'wave',
      'point',
      'coffee',
      'sign',
      'scene',
      'zoom-across', // Donkey ride-by!
      'grab-element', // Grab site elements
    ];

    // Extra chance for zoom-across (total ~15% of actions)
    if (Math.random() < 0.25) {
      actions.push('zoom-across');
    }
    // Rare sleep
    if (Math.random() < 0.05) {
      actions.push('sleep');
    }

    const action = actions[Math.floor(Math.random() * actions.length)];
    setCurrentAction(action);

    switch (action) {
      case 'walk': {
        // Random horizontal walk, keep Y
        const maxX = window.innerWidth - 100;
        const targetX = 20 + Math.random() * (maxX - 40);
        walkTo({ x: targetX, y: position.y });
        break;
      }

      case 'climb': {
        // Move to a new vertical position
        const targetY = getRandomY();
        const targetX = position.x + (Math.random() - 0.5) * 100;
        walkTo({
          x: Math.max(20, Math.min(window.innerWidth - 100, targetX)),
          y: targetY
        });
        break;
      }

      case 'speak': {
        const useContextual = Math.random() < 0.4;
        const message = useContextual
          ? getSectionMessage(currentSection)
          : getRandomMessage(RANDOM_MESSAGES);
        setAnimation('idle');
        showMessage(message);
        setTimeout(resetToIdle, 4000);
        break;
      }

      case 'wave': {
        setAnimation('waving');
        setTimeout(() => showMessage("Hey there!"), 500);
        setTimeout(resetToIdle, 3000);
        break;
      }

      case 'point': {
        setAnimation('pointing');
        // Lock to a random section
        const sections = ['projects', 'patents', 'experience', 'contact'];
        const targetSection = sections[Math.floor(Math.random() * sections.length)];
        const newY = lockToElement(targetSection);
        if (newY) {
          setPosition(prev => ({ ...prev, y: newY }));
        }
        showMessage("Check this out!");
        setTimeout(resetToIdle, 3000);
        break;
      }

      case 'sleep': {
        setAnimation('sleeping');
        setTimeout(resetToIdle, 6000);
        break;
      }

      case 'coffee': {
        setActiveProp('coffee');
        setAnimation('sipping');
        showMessage("Coffee break!");
        setTimeout(resetToIdle, 4000);
        break;
      }

      case 'sign': {
        setIsHoldingSign(true);
        setAnimation('pushing'); // Use pushing animation for holding
        showMessage("HIRE ME!");
        setTimeout(() => {
          // Spin the sign
          setActiveProp('sign');
        }, 500);
        setTimeout(resetToIdle, 5000);
        break;
      }

      case 'zoom-across': {
        zoomAcross('donkey');
        break;
      }

      case 'scene': {
        const scenes: SceneType[] = ['aquarium', 'forest', 'beach', 'mycelium'];
        const randomScene = scenes[Math.floor(Math.random() * scenes.length)];
        setActiveScene(randomScene);
        setAnimation('idle');

        const sceneMessages: Record<SceneType, string> = {
          aquarium: "Look at the fishies!",
          forest: "Nature is cool",
          beach: "Wish I was there...",
          mycelium: "Bio-computing!",
        };
        showMessage(sceneMessages[randomScene]);
        setTimeout(resetToIdle, 5000);
        break;
      }

      case 'grab-element': {
        const closest = findClosestGrabbableElement();
        if (closest) {
          // Put on hardhat and walk to the element
          setIsWearingHardhat(true);
          setIsInteracting(true);
          pendingGrabRef.current = closest.element;

          // Walk to the element
          walkTargetRef.current = { x: closest.targetX, y: closest.targetY };
          setCurrentAction('walk-to-grab');
          setAnimation('walking');
          setDirection(closest.targetX > position.x ? 'right' : 'left');

          showMessage("Time to help!");
        } else {
          // No grabbable elements, just idle
          setAnimation('idle');
          setCurrentAction('idle');
        }
        break;
      }
    }
  }, [isInteracting, isOffscreen, position, currentSection, walkTo, showMessage, resetToIdle, getRandomY, lockToElement, zoomAcross, findClosestGrabbableElement]);

  // Walking animation loop (handles both X and Y)
  useEffect(() => {
    if (currentAction !== 'walk' && currentAction !== 'climb' && currentAction !== 'walk-to-grab') return;
    if (!walkTargetRef.current) return;

    const target = walkTargetRef.current;
    const speed = 2.5;
    const isWalkingToGrab = currentAction === 'walk-to-grab';

    const walkInterval = setInterval(() => {
      setPosition(prev => {
        const dx = target.x - prev.x;
        const dy = target.y - prev.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < speed) {
          walkTargetRef.current = null;

          // If we were walking to grab something, now grab it
          if (isWalkingToGrab && pendingGrabRef.current) {
            grabElement(pendingGrabRef.current);
            pendingGrabRef.current = null;
          } else {
            setAnimation('idle');
            setCurrentAction('idle');
          }
          return { x: target.x, y: target.y };
        }

        // Normalize and apply speed
        const vx = (dx / distance) * speed;
        const vy = (dy / distance) * speed;

        // Update direction based on horizontal movement
        if (Math.abs(dx) > 0.5) {
          setDirection(dx > 0 ? 'right' : 'left');
        }

        return {
          x: prev.x + vx,
          y: prev.y + vy,
        };
      });
    }, 16);

    return () => clearInterval(walkInterval);
  }, [currentAction, grabElement]);

  // Schedule random actions
  useEffect(() => {
    const scheduleNextAction = () => {
      const delay = 2000 + Math.random() * 4000; // 2-6 seconds (faster actions!)
      actionTimeoutRef.current = setTimeout(() => {
        if (currentAction === 'idle' && !isOffscreen && !isDragging) {
          pickRandomAction();
        }
        scheduleNextAction();
      }, delay);
    };

    scheduleNextAction();

    return () => {
      if (actionTimeoutRef.current) {
        clearTimeout(actionTimeoutRef.current);
      }
    };
  }, [currentAction, isOffscreen, isDragging, pickRandomAction]);

  // Speech bubble completion handler
  const handleSpeechComplete = useCallback(() => {
    setShowSpeech(false);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`daniel-controller ${isOffscreen ? 'offscreen' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* Dust Cloud (behind everything) */}
      {showDustCloud && (
        <div className={`dust-cloud ${direction}`}>
          <span className="dust dust-1"></span>
          <span className="dust dust-2"></span>
          <span className="dust dust-3"></span>
          <span className="dust dust-4"></span>
        </div>
      )}

      {/* Active Scene (behind character) */}
      {activeScene && (
        <div className="active-scene">
          <PixelScene type={activeScene} />
        </div>
      )}

      {/* Vehicle Mount */}
      {vehicle === 'donkey' && (
        <div className="vehicle-mount">
          <PixelProp type="donkey" active={true} direction={direction} />
        </div>
      )}

      {/* Main Character */}
      <div
        className={`character-wrapper ${vehicle !== 'none' ? 'on-vehicle' : ''} ${isHoldingSign ? 'holding-sign' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          transform: `rotate(${dragRotation}deg)`,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <PixelDaniel
          animation={animation}
          direction={direction}
          wearingHardhat={isWearingHardhat}
        />

        {/* Speech Bubble */}
        <SpeechBubble
          message={speechMessage}
          visible={showSpeech}
          position="above"
          onComplete={handleSpeechComplete}
        />

        {/* Held Props (attached to character) */}
        {isHoldingSign && (
          <div className="held-prop sign-held">
            <PixelProp type="sign" active={true} />
          </div>
        )}

        {/* Coffee held in left hand */}
        {activeProp === 'coffee' && (
          <div className="held-prop coffee-held">
            <PixelProp type="coffee" active={true} />
          </div>
        )}
      </div>

      {/* Other props next to character */}
      {activeProp && activeProp !== 'donkey' && activeProp !== 'coffee' && !isHoldingSign && (
        <div className="side-prop">
          <PixelProp type={activeProp} active={activeProp === 'sign'} />
        </div>
      )}

      {/* Grabbed Element */}
      {grabbedElement && (
        <div
          className="grabbed-element"
          style={{
            width: `${grabbedElement.width}px`,
            height: `${grabbedElement.height}px`,
          }}
          dangerouslySetInnerHTML={{ __html: grabbedElement.html }}
        />
      )}
    </div>
  );
};

export default DanielController;
